import Grid from '@mui/material/Grid2';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import { StackProps, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import { Fragment } from 'react';
import { TCustomTheme } from '@mgm/common/theme';
import { useCustomTheme } from '@mgm/common/hooks/useCustomTheme';
import { Iconify } from '@mgm/common/components';
import { useAppDispatch, useAppSelector } from '../../../../hooks/useAppStore';
import { getSelectedCommissionPlan } from '../../../../store/commissions/selectors';
import { commissionsActions } from '../../../../store/commissions/slice';
import { BaseChip } from '../../../../components/base';

interface PlanCardProps extends StackProps {
  plan: {
    title: string;
    value: 'Fixed' | 'Steps' | 'Products'
    examples: string[];
  };
}

function PlanCard({ plan, sx, ...other }: PlanCardProps) {
  const {
    title, value, examples,
  } = plan;

  const theme = useTheme() as TCustomTheme;

  const { isMobile } = useCustomTheme(theme);

  const selectedCommissionPlan = useAppSelector(getSelectedCommissionPlan);
  const dispatch = useAppDispatch();

  const { t } = useTranslation('commissions');

  const handleSelectPlan = () => {
    dispatch(commissionsActions.setSelectedCommissionsPlan({
      ...selectedCommissionPlan,
      type: value,
    }));
  };

  const fixed = value === 'Fixed';

  const steps = value === 'Steps';

  return (
    <Stack
      spacing={5}
      onClick={handleSelectPlan}
      sx={{
        cursor: 'pointer',
        p: 5,
        pt: 6,
        WebkitTapHighlightColor: 'transparent',
        ...(steps && !isMobile && {
          borderLeft: (theme) => `dashed 1px ${theme.palette.divider}`,
          ...sx,
        }),
      }}
      {...other}
    >
      <Stack spacing={2}>
        <Box display="flex" justifyContent="flex-end">
          <IconButton>
            {selectedCommissionPlan.type === value ? (
              <Iconify
                icon="gg:check-o"
                width={30}
                sx={{
                  color: steps ? '#005249' : '#A855F7',
                }}
              />
            ) : (
              <Iconify
                icon="ic:outline-radio-button-unchecked"
                width={30}
                sx={{
                  color: steps ? '#005249' : '#A855F7',
                }}
              />
            )}
          </IconButton>
        </Box>
        <Stack spacing={2} alignItems="center" direction="row" justifyContent="center">
          <Iconify
            icon={fixed ? 'mdi:lock-rate-outline' : 'lucide-lab:stairs-arrow-up-right'}
            width={30}
            sx={{
              color: steps ? '#005249' : '#A855F7',
            }}
          />
          <Typography color={steps ? '#005249' : 'primary'} variant="h4">{title}</Typography>
          {selectedCommissionPlan?.type === value && (
          <BaseChip
            color={selectedCommissionPlan?.type === 'Fixed' ? '#A855F7' : '#005249'}
            label={t('selected')}
          />
          )}
        </Stack>

        <Typography
          color="text.secondary"
          textAlign="center"
          variant="subtitle2"
        >
          {fixed ? (
            <>
              {t('thisPlanProvidesALinear')}
            </>
          ) : (
            <>
              {t('thisPlanProvidesADynamic')}
            </>
          )}
        </Typography>
      </Stack>
      <Divider sx={{ borderStyle: 'dashed', borderColor: steps ? '#005249' : '#A855F7' }} />

      <Stack spacing={2.5}>
        <Typography
          color={steps ? '#005249' : '#A855F7'}
          variant="subtitle2"
        >
          {fixed ? t('fixedCompensation') : t('dynamicCompensation')}
        </Typography>
        <Typography
          color={steps ? '#005249' : '#A855F7'}
          variant="subtitle2"
        >
          {fixed ? t('commissionPerSales') : t('goalsAreMetBasedOnTotal')}
        </Typography>

        <Divider sx={{ borderStyle: 'dashed', borderColor: steps ? '#005249' : '#A855F7' }} />

        <Typography variant="subtitle1">{t('bestSuitedForScenariosLike')}</Typography>
        {examples.map((option) => (
          <Fragment key={option}>
            <Stack spacing={1} direction="row" alignItems="center">
              <Typography variant="body2">{option}</Typography>
            </Stack>
            <Divider sx={{ borderStyle: 'dashed', borderColor: steps ? '#005249' : '#A855F7' }} />
          </Fragment>
        ))}
      </Stack>
    </Stack>
  );
}

const SelectCommissionPlan = () => {
  const { t } = useTranslation('commissions');

  const theme = useTheme() as TCustomTheme;

  const { isMobile } = useCustomTheme(theme);

  const PLANS = [t('fixedPlan'), t('stepsPlan')];
  const PLANS_VALUE = ['Fixed', 'Steps'];

  const EXAMPLES: { [key: number]: Array<string> } = {
    0: [t('GiveMyEmployeeFive'), t('GiveMyEmployeeFivePercent')],
    1: [t('GiveMyEmployeeFiveHeSold'), t('GiveMyEmployeeFiveHeSoldOver')],
  };

  const commissionPlans = [...Array(2)].map((_, index) => ({
    title: PLANS[index],
    value: PLANS_VALUE[index] as 'Fixed' | 'Steps' | 'Products',
    examples: EXAMPLES[index],
  }));

  return (
    <Grid
      container
      mb={8}
      sx={{ borderRadius: 2, mt: 5, border: (theme) => `dashed 1px ${theme.palette.divider}` }}
    >
      {commissionPlans.map((plan, index) => (
        <Grid
          key={plan.value}
          sx={{
            ...(isMobile && index === 1 && {
              borderTop: (theme) => `dashed 1px ${theme.palette.divider}`,
            }),
          }}
          size={{
            xs: 12,
            sm: 6,
            md: 6,
            lg: 6,
          }}
        >
          <PlanCard plan={plan} />
        </Grid>
      ))}
    </Grid>
  );
};

export {
  SelectCommissionPlan,
};
